/**
 * useAnalytics — React hook wrapping analyticsService.
 *
 * Provides trackPageView (auto-fires on route change) and trackTranslation
 * for use in components without importing the service directly.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackPageView as rawTrackPageView,
  trackTranslation as rawTrackTranslation,
  trackEvent as rawTrackEvent,
} from '../services/analyticsService';

/**
 * Hook that automatically tracks page views on route changes
 * and exposes analytics helpers for components.
 */
export function useAnalytics() {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  // Auto-track page views on route change (deduped)
  useEffect(() => {
    if (location.pathname !== previousPath.current) {
      previousPath.current = location.pathname;
      rawTrackPageView(location.pathname, document.title);
    }
  }, [location.pathname]);

  /**
   * Track a completed translation.
   * Call after a successful translation response.
   */
  const trackTranslation = useCallback(
    (
      sourceLang: string,
      targetLang: string,
      mode: string,
      latencyMs: number
    ) => {
      rawTrackTranslation(sourceLang, targetLang, mode, latencyMs);
    },
    []
  );

  /**
   * Track a custom event.
   */
  const trackEvent = useCallback(
    (name: string, params?: Record<string, string | number | boolean | undefined>) => {
      rawTrackEvent(name, params);
    },
    []
  );

  /**
   * Track a page view manually (for non-route-change scenarios).
   */
  const trackPageView = useCallback(
    (path: string, title?: string) => {
      rawTrackPageView(path, title);
    },
    []
  );

  return { trackPageView, trackTranslation, trackEvent };
}
